import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const navClass = 'absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4B23E] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/10 sm:flex';
export function PageLightbox({
  pages,
  index,
  onIndexChange,
  onClose
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragged = useRef(false);
  const [direction, setDirection] = useState(0);
  const reduceMotion = useReducedMotion();
  const open = index !== null;
  const total = pages.length;
  const go = (next: number) => {
    if (index === null || next < 0 || next >= total) return;
    setDirection(next > index ? 1 : -1);
    onIndexChange(next);
  };
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') go(index - 1);
      if (e.key === 'ArrowRight') go(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const onDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (index === null) return;
    const swipe = info.offset.x + info.velocity.x * 0.2;
    if (swipe < -60) go(index + 1);else if (swipe > 60) go(index - 1);
    window.setTimeout(() => {
      dragged.current = false;
    }, 0);
  };
  const page = index !== null ? pages[index] : null;
  const slide = reduceMotion ? 0 : 48;
  return <AnimatePresence data-id="element-170" data-mp="n4zp8wfgsetf">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Page ${index + 1} of ${total}`} className="fixed inset-0 z-50 flex flex-col bg-[#15213A]/95" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={() => {
      if (!dragged.current) onClose();
    }} data-id="element-171" data-mp="n5htaf8dbxbw">
          <div className="flex items-center justify-between px-4 py-3 sm:px-6" data-id="element-172" data-mp="23k11ef72awzz">
            <p className="text-sm tabular-nums text-white/70" aria-live="polite" data-id="element-173" data-mp="2b30lyo69eukx">
              <span className="font-semibold text-white" data-id="element-174" data-mp="2b5dajk6iw36y">{index + 1}</span> / {total}
            </p>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close full-screen view" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4B23E]" data-id="element-175" data-mp="2fzqyx2mrpa24">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-176" data-mp="2k3p0rl3h8yyv" />
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 sm:px-20" data-id="element-177" data-mp="2p2s0qr4loezz">
            <button type="button" onClick={e => {
          e.stopPropagation();
          go(index - 1);
        }} disabled={index === 0} aria-label="Previous page" className={`${navClass} left-4`} data-id="element-178" data-mp="3cruhlwreu3tf">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-179" data-mp="1ov51uefsc8hb" />
            </button>

            <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-180" data-mp="1jw1wstzt229c">
              <motion.img key={page.src} src={page.src} alt={page.caption} draggable={false} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.6} onDragStart={() => {
            dragged.current = true;
          }} onDragEnd={onDragEnd} onClick={e => e.stopPropagation()} className="max-h-full w-auto max-w-full cursor-grab touch-pan-y select-none rounded-xl object-contain active:cursor-grabbing" initial={{
            opacity: 0,
            x: direction * slide
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -direction * slide
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} data-id="element-181" data-mp="1exid708op2k5" />
            </AnimatePresence>

            <button type="button" onClick={e => {
          e.stopPropagation();
          go(index + 1);
        }} disabled={index === total - 1} aria-label="Next page" className={`${navClass} right-4`} data-id="element-182" data-mp="32xccnw6z1ied">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-183" data-mp="3hqpjfoxs9z9q" />
            </button>
          </div>

          <p className="mx-auto max-w-md px-6 pb-6 pt-4 text-center text-[15px] leading-relaxed text-white/80" data-id="element-184" data-mp="3qku5lv1z2mix">
            {page.caption}
          </p>
        </motion.div>}
    </AnimatePresence>;
}